<script setup lang="ts">
import { computed } from 'vue'

import { Button, Checkbox, Radio, Tooltip } from '@collector/shared-ui'
import { nullCoalesce } from '@collector/shared-utils'

import type {
  LineupConfig,
  PositionConfig,
} from '../../../../interfaces/LineupConfig'
import type { LineupPlayer } from '../../../../interfaces/LineupModel'
import { isCaptain } from '../../../../utils/player.utils'
import ShirtNrTextInput from '../../../common/ShirtNrTextInput.vue'
import PlayerName from '../PlayerName.vue'
import PositionSectionSortHandle from './PositionSectionSortHandle.vue'

type Props = {
  config: PositionConfig
  lineupConfig: LineupConfig
  sortable?: boolean
}
const props = defineProps<Props>()

type Emits = {
  capitanUpdated: [player: LineupPlayer]
  delete: [player: LineupPlayer]
}
const emit = defineEmits<Emits>()

// state
const player = defineModel<LineupPlayer>({ required: true })

// computed
const isPlayerCaptain = computed(() => isCaptain(player.value))
const isShirtNrInputEnabled = computed(() =>
  nullCoalesce(props.config.isShirtNrInputEnabled, true),
)
</script>

<template>
  <div
    class="bg-neutral-light-10 flex items-center gap-2 rounded-md p-1"
    data-testid="lineups-position-row"
  >
    <div class="w-12 shrink-0">
      <ShirtNrTextInput
        v-if="config.showShirtNr"
        :withValidationStyle="isShirtNrInputEnabled"
        :modelValue="player.shirt_nr"
        :disabled="!isShirtNrInputEnabled"
        @update:modelValue="player = { ...player, shirt_nr: $event }"
      />
    </div>
    <div class="grow">
      <PlayerName
        :player
        :prefix="config.rowPrefix"
        :highlightMissingId="lineupConfig.display.highlightMissingId"
      />
    </div>

    <Checkbox
      v-if="config.secondaryPosition"
      :modelValue="player.has_secondary_position"
      @update:modelValue="
        player = { ...player, has_secondary_position: $event }
      "
    >
      <Tooltip lazy>
        <span class="w-20">{{ config.secondaryPosition }}</span>
        <template #tooltip>
          possible
          {{ config.secondaryPosition === 'PIT' ? 'pitcher' : 'batter' }}
        </template>
      </Tooltip>
    </Checkbox>
    <PositionSectionSortHandle v-if="sortable" />

    <Radio
      v-if="config.showCaptainRadio"
      name="captain"
      label="C"
      :modelValue="isPlayerCaptain"
      size="xs"
      @update:modelValue="emit('capitanUpdated', player)"
    />
    <Button
      class="bg-error-6 hover:bg-error-8 text-neutral-light-10 rounded-md!"
      :visible="nullCoalesce(config.showDeleteButton, true)"
      icon="close"
      square
      iconSize="lg"
      size="xs"
      @click="emit('delete', player)"
    />
  </div>
</template>

<style scoped></style>
